/**
 * EO «Конфигуратор → Услуги» — справочник общесетевых доп. услуг (A38/A41)
 * по эталону design-references/screen-18-eo-config-prices-mk.html (панель
 * #paneMk) + screen-18-prices.js (renderMkForm/renderMkList js:273-337).
 *
 * Карточки: имя + short-чип + «N мин» + OpsMini + ✕. Тап по строке — форма
 * правки внутри карточки; ✕ — НЕМЕДЛЕННОЕ удаление с undo-снекбаром 5с
 * (deferred DELETE с keepalive — контракт UndoSnackbar, A43.2); свайп-вправо
 * — форма создания с копией всех полей «{имя} (копия)» (A43.1, SwipeDup);
 * pull-to-refresh (A43.3). Справка-«?» — TipBox с текстом эталона ДОСЛОВНО
 * (js:399-403). Кнопки «Сохранить» у цен нет — авто-сейв A38.
 */
import { useEffect, useRef, useState, type MouseEvent } from "react";
import type { MasterClass } from "../../api/types";
import { PullHud, useUndoSnackbar } from "../../components/shared";
import { useAppData } from "../../hooks/useAppData";
import { useAppReload, usePullToRefresh } from "../../hooks/usePullToRefresh";
import { useServicesAdmin } from "../../hooks/useServicesAdmin";
import { crewToOps, OpsMini } from "./OpsRow";
import { TipBox } from "./PriceRow";
import { CrossIco, PlusIco } from "./questIcons";
import { ServiceForm, svc0, svcDraft, svcFromService, type SvcFormState } from "./ServiceForm";
import { SwipeDup } from "./SwipeDup";
import cfg from "./config.module.css";
import pcss from "./prices.module.css";
import qcss from "./quests.module.css";
import css from "./services.module.css";

/** Перо шапки (эталон H:426). */
const FeatherIco = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 4c-4 0-9 2-12 7l-2 5 5-2c5-3 7-8 7-12z" />
    <path d="M7 15l2 2" />
    <path d="M4 20c1-2 3-2 4-1" />
  </svg>
);

export function EoConfigServices({ onDirtyChange }: { onDirtyChange?: (d: boolean) => void } = {}) {
  const app = useAppData();
  const sa = useServicesAdmin();
  const snack = useUndoSnackbar();
  const [formOpen, setFormOpen] = useState(false);
  const [cSt, setCSt] = useState<SvcFormState>(svc0);
  const [cBusy, setCBusy] = useState(false);
  /* Фокус «Название» — только при открытии кнопкой «Добавить» (js:370-371);
     дубликат-свайп открывает форму без фокуса (эталон js:335). */
  const [cFocus, setCFocus] = useState(true);
  const [editId, setEditId] = useState<string | null>(null);
  const [eSt, setESt] = useState<SvcFormState>(svc0);
  const [tip, setTip] = useState<HTMLElement | null>(null);
  const scRef = useRef<HTMLDivElement | null>(null);
  const reloadApp = useAppReload();
  const ptr = usePullToRefresh(() => { sa.refetch(); return reloadApp(); });
  const setScroll = (el: HTMLDivElement | null) => { scRef.current = el; ptr.ref(el); };

  /* 31.08: открытая форма (правка или создание) = несохранённая правка —
     смена суб-таба конфигуратора спрашивает подтверждение, как у квестов. */
  useEffect(() => {
    onDirtyChange?.(formOpen || editId !== null);
    return () => onDirtyChange?.(false);
  }, [formOpen, editId, onDirtyChange]);

  const toggleCreate = () => { setFormOpen((o) => !o); setCBusy(false); setCSt(svc0()); setCFocus(true); };
  const tipToggle = (e: MouseEvent<HTMLButtonElement>) => {
    const anchor = e.currentTarget;
    setTip((t) => (t ? null : anchor));
  };

  const submitCreate = () => {
    if (cBusy || !cSt.name.trim()) return;
    setCBusy(true);
    void sa.create(svcDraft(cSt)).then((id) => {
      setCBusy(false);
      if (id) { setFormOpen(false); setCSt(svc0()); }
    });
  };

  const openEdit = (m: MasterClass) => {
    setEditId(m.id);
    setESt(svcFromService(m, sa.consOf(m.id), sa.resOf(m.id)));
  };

  /* «Сохранить» — optimistic (эталон js:322-324): форма закрывается сразу,
     ошибка сети вернёт значения с красным hint (run-паттерн); стёртое имя
     падает на старое (js:323: m.n = st.n.trim() || m.n). */
  const submitEdit = (m: MasterClass) => {
    const d = svcDraft(eSt);
    void sa.save(m, { ...d, name: d.name || m.name });
    setEditId(null);
  };

  /* A43.1: свайп-вправо — форма создания с копией всех полей исходника. */
  const dup = (m: MasterClass) => {
    setCSt({
      ...svcFromService(m, sa.consOf(m.id), sa.resOf(m.id)),
      /* slice: sanitize name бросает 400 при >200 — длинное имя + «(копия)» */
      name: `${m.name} (копия)`.slice(0, 200),
    });
    setCBusy(false); setFormOpen(true); setEditId(null); setCFocus(false);
    if (scRef.current) scRef.current.scrollTop = 0;
  };

  /* A43.2: ✕ — строка сразу прячется, DELETE отложен на снекбар. */
  const del = (m: MasterClass) => {
    sa.hide(m.id);
    if (editId === m.id) setEditId(null);
    snack.show({
      label: `Услуга «${m.name}» удалена`,
      onUndo: () => sa.unhide(m.id),
      onExpire: () => sa.removeNow(m),
    });
  };

  /* Провал загрузки НЕ рендерит форму с пустыми справочниками и нулями цен
     (replace-set перетёр бы БД) — скелетон + errHint; PTR работает и здесь. */
  if ((app.loading && app.masterClasses.length === 0) || !sa.loaded) {
    return (
      <div className={cfg.scroll} ref={setScroll}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        {sa.loadErr ? (
          <div className={cfg.errHint} style={{ margin: "8px 10px 0" }}>
            Услуги не загрузились — потяните вниз, чтобы повторить.
          </div>
        ) : null}
        <div className={cfg.skel}>
          <div className={`${cfg.sk} ${cfg.skTitle}`} />
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={`${cfg.sk} ${cfg.skTea}`} />)}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={cfg.scroll} ref={setScroll}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        <div className={qcss.s17head} style={{ marginTop: 5 }}>
          <span className={css.headIco}><FeatherIco /></span>
          <h2 className={cfg.secT}>Услуги ({sa.services.length})</h2>
          <button type="button" className={pcss.qmark} data-qmark="" aria-label="Справка" onClick={tipToggle}>
            <span>?</span>
          </button>
          <button type="button" className={`${qcss.abtn} ${qcss.cf} ${qcss.solo}`} onClick={toggleCreate}>
            {formOpen ? <><CrossIco w={11} />Отмена</> : <><PlusIco />Добавить</>}
          </button>
        </div>
        {sa.loadErr ? (
          <div className={cfg.errHint} style={{ margin: "0 8px" }}>
            Часть данных не загрузилась — эти правки заблокированы; потяните вниз, чтобы обновить.
          </div>
        ) : null}
        {sa.hint ? <div className={cfg.errHint} style={{ margin: "0 8px" }}>{sa.hint}</div> : null}
        {formOpen ? (
          <div className={qcss.cform}>
            <div className={cfg.secRow}>
              <h3 className={`${cfg.secT} ${qcss.cformT}`}>Новая услуга</h3>
            </div>
            <ServiceForm
              st={cSt} setSt={setCSt} sa={sa} busy={cBusy} autoFocusName={cFocus}
              okLabel={cBusy ? "Создаём…" : "Создать"} onOk={submitCreate} onCancel={toggleCreate}
            />
          </div>
        ) : null}
        {sa.services.map((m) => {
          const editing = editId === m.id;
          const f = sa.flash?.key === `svc:${m.id}` ? sa.flash : null;
          const ops = crewToOps(m.operators_comp);
          return (
            <article
              /* 31.08: у открытой формы key стабилен — смена ключа при
                 сбросе flash (через 900мс) ремаунтила форму: закрывался
                 NumPad цен и сворачивалась клавиатура посреди набора. */
              key={editing ? m.id : f ? `${m.id}:${f.n}` : m.id}
              className={`${css.mcard} ${css.mcCard} ${editing ? css.editing : ""} ${f ? cfg.flash : ""}`}
            >
              {editing ? (
                <div className={css.medit}>
                  <ServiceForm
                    st={eSt} setSt={setESt} sa={sa} editId={m.id}
                    okLabel="Сохранить" onOk={() => submitEdit(m)} onCancel={() => setEditId(null)}
                  />
                </div>
              ) : (
                <SwipeDup disabled={false} onDup={() => dup(m)} className={css.mrow} onClick={() => openEdit(m)}>
                  <span className={css.mn}>{m.name}</span>
                  {m.short_name ? <span className={css.shch}>{m.short_name}</span> : null}
                  {m.duration ? (
                    <span className={css.mm}><span className={qcss.num}>{m.duration}</span>&nbsp;мин</span>
                  ) : null}
                  {ops.length ? <OpsMini ops={ops} /> : null}
                  <button type="button" className={qcss.tx} title="Удалить" onClick={(e) => { e.stopPropagation(); del(m); }}>
                    <CrossIco />
                  </button>
                </SwipeDup>
              )}
            </article>
          );
        })}
        {!sa.services.length ? (
          <div className={cfg.teaEmpty}>
            {app.error ? "Список услуг не загрузился — потяните вниз, чтобы повторить." : "Нет доп. услуг — добавьте первую."}
          </div>
        ) : null}
      </div>
      {tip ? (
        <TipBox anchor={tip} onClose={() => setTip(null)}>
          {/* Текст эталона ДОСЛОВНО (js:400-403). */}
          <p>Справочник общесетевой — услуги без филиала. Тап по строке — редактировать.</p>
          <p><b>Свайп вправо</b> по строке — дубликат: откроется форма копии.</p>
          <p><b>✕</b> удаляет сразу — внизу 5 секунд можно ОТМЕНИТЬ.</p>
          <p><b>Потяните вниз</b> от верха списка — обновить данные.</p>
        </TipBox>
      ) : null}
      {snack.element}
    </>
  );
}
